/**
 * ============================================
 * COMPONENT: MpCrossProductSwatchesPicker
 * PURPOSE: Handles cross-product color swatch interactions
 *          for product cards and PDP. Swaps image, URL, price, and
 *          variant ID when a different color product is
 *          hovered or selected. Integrates with quick-add
 *          so the correct product is added to cart.
 *          On PDP (no product-card parent), navigates to
 *          the selected product page instead.
 * ADDED: 2025-02-13 by MeshPlanet
 * UPDATED: 2026-02-17 - Added PDP navigation support
 * ============================================
 */

import { OverflowList } from '@theme/overflow-list';
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Custom element that manages cross-product color swatches.
 * Each swatch represents a different product (color variation).
 *
 * @typedef {object} Refs
 * @property {OverflowList} overflowList - The overflow list component.
 *
 * @extends {Component<Refs>}
 */
class MpCrossProductSwatchesPicker extends Component {
  /** @type {import('./product-card').ProductCard | null} */
  #productCard = null;

  /** @type {string | null} The product ID currently committed (selected by click) */
  #committedProductId = null;

  /** @type {string | null} The variant ID currently committed (for same-product variant navigation) */
  #committedVariantId = null;

  /** @type {Array<{hidden: boolean, src: string, srcset: string, alt: string}>|null} */
  #originalSlideState = null;

  /** @type {HTMLElement|null} The span that displays the current color name */
  #colorNameEl = null;

  /** @type {string} The color name of the committed (selected) swatch */
  #committedColorName = '';

  connectedCallback() {
    super.connectedCallback();

    this.#productCard = this.closest('product-card');

    // Store the initially checked product and variant as the committed ones
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      this.#committedProductId = checkedInput.dataset.mpProductId || null;
      this.#committedVariantId = checkedInput.dataset.variantId || null;
    }

    // Find the color name label element in the parent mp-color-swatches block
    const parentBlock = this.closest('mp-color-swatches');
    if (parentBlock) {
      this.#colorNameEl = parentBlock.querySelector('.mp-color-swatches__color-name');
      if (this.#colorNameEl) {
        this.#committedColorName = this.#colorNameEl.textContent.trim();
      }
    }

    // Listen for radio change (click selection)
    this.addEventListener('change', this.#handleSwatchSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleSwatchSelect);
  }

  /**
   * Preview a product image on swatch hover.
   * Called via on:pointerenter="/mpPreviewProduct"
   * @param {PointerEvent} event
   */
  mpPreviewProduct(event) {
    if (event.pointerType !== 'mouse') return;

    this.mpResetProduct.cancel();

    const label = event.target instanceof Element ? event.target.closest('label') : null;
    const input = label?.querySelector('input');
    if (!input) return;

    const imageUrl = input.dataset.mpProductImage;
    const imageAlt = input.dataset.mpProductImageAlt;
    if (imageUrl) {
      this.#swapGalleryImage(imageUrl, imageAlt);
    }

    // Update color name label on hover
    const swatchLi = label.closest('.variant-option__swatch');
    const colorValue = swatchLi?.dataset.mpColorValue;
    if (colorValue && this.#colorNameEl) {
      this.#colorNameEl.textContent = colorValue;
    }
  }

  /**
   * Reset the product card image to the committed (clicked) product.
   * Called via on:pointerleave="/mpResetProduct"
   * Debounced to prevent flickering between swatches.
   */
  mpResetProduct = debounce(() => {
    if (!this.#committedProductId) return;

    // Use variant ID for precise matching when multiple swatches share the same product ID
    let selector = `input[data-mp-product-id="${this.#committedProductId}"]`;
    if (this.#committedVariantId) {
      selector += `[data-variant-id="${this.#committedVariantId}"]`;
    }
    const committedInput = this.querySelector(selector);
    if (!committedInput) return;

    const imageUrl = committedInput.dataset.mpProductImage;
    const imageAlt = committedInput.dataset.mpProductImageAlt;
    if (imageUrl) {
      this.#swapGalleryImage(imageUrl, imageAlt);
    }

    // Restore committed color name
    if (this.#colorNameEl) {
      this.#colorNameEl.textContent = this.#committedColorName;
    }
  }, 100);

  /**
   * Handle swatch click selection. Updates the committed product,
   * swaps image, URL, price, and variant ID permanently.
   * On PDP (no product-card parent), navigates to the product page.
   * @param {Event} event
   */
  #handleSwatchSelect = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.mpProductId) return;

    // PDP context: no product card parent, navigate to the product page
    if (!this.#productCard) {
      // Skip navigation if clicking the already-active product and variant
      if (
        input.dataset.mpProductId === this.#committedProductId &&
        input.dataset.variantId === this.#committedVariantId
      ) return;

      const productUrl = input.dataset.mpProductUrl;
      const variantId = input.dataset.variantId;
      if (productUrl) {
        const url = new URL(productUrl, window.location.origin);
        if (variantId) {
          url.searchParams.set('variant', variantId);
        }
        window.location.replace(url.pathname + url.search);
      }
      return;
    }

    this.#committedProductId = input.dataset.mpProductId;
    this.#committedVariantId = input.dataset.variantId || null;

    // Update committed color name on click
    const swatchLi = input.closest('.variant-option__swatch');
    const colorValue = swatchLi?.dataset.mpColorValue;
    if (colorValue) {
      this.#committedColorName = colorValue;
      if (this.#colorNameEl) {
        this.#colorNameEl.textContent = colorValue;
      }
    }

    // Save original gallery state before first swap so we can restore it later
    this.#saveOriginalSlideState();

    // Check if selecting the original product — restore the full server-rendered gallery
    const originalProductId = this.#productCard.querySelector('.card-gallery')?.dataset?.productId;
    const imageUrl = input.dataset.mpProductImage;

    if (input.dataset.mpProductId === originalProductId) {
      this.#restoreOriginalSlides();
    } else {
      // Update gallery images and hide stale slides from the previous product
      const image2Url = input.dataset.mpProductHoverImage;
      const imageAlt = input.dataset.mpProductImageAlt;
      if (imageUrl) {
        this.#swapGalleryImage(imageUrl, imageAlt, image2Url);
        this.#hideExtraSlides(image2Url ? 2 : 1);
      }
    }

    // Update product URLs (includes variant param for quick-add)
    const productUrl = input.dataset.mpProductUrl;
    const variantId = input.dataset.variantId;
    if (productUrl) {
      this.#updateProductUrl(productUrl, variantId);
    }

    // Update the hidden variant ID input for add-to-cart form
    if (variantId) {
      this.#updateVariantIdInput(variantId, input.dataset.mpVariantAvailable === 'true');
    }

    // Update price display
    this.#updatePrice(input);

    // Update view transition featured media URL
    const productCardLink = this.#productCard?.closest('product-card-link');
    if (productCardLink && imageUrl) {
      productCardLink.setAttribute('data-featured-media-url', imageUrl);
    }

    // Clear quick-add cache so it fetches the new product page
    const quickAdd = this.#productCard?.querySelector('quick-add-component');
    if (quickAdd) {
      quickAdd.fetchProductPage(this.#buildQuickAddUrl(productUrl, variantId));
    }
  };

  /**
   * Build the quick-add product page URL with variant parameter.
   * @param {string} productUrl - The product URL
   * @param {string} [variantId] - The variant ID
   * @returns {string}
   */
  #buildQuickAddUrl(productUrl, variantId) {
    const url = new URL(productUrl, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    }
    return url.toString();
  }

  /**
   * Swap the gallery images (first slide + second slide for hover preview).
   * @param {string} imageUrl - The new primary image URL
   * @param {string} [altText] - The new alt text
   * @param {string} [image2Url] - The new second image URL for hover preview
   */
  #swapGalleryImage(imageUrl, altText, image2Url) {
    if (!this.#productCard) return;

    // Find all images in the card gallery slides
    const galleryImages = this.#productCard.querySelectorAll('.card-gallery slideshow-slide img');

    // Update first slide image
    const firstImg = galleryImages[0];
    if (firstImg instanceof HTMLImageElement) {
      firstImg.src = imageUrl;
      firstImg.srcset = imageUrl;
      if (altText) {
        firstImg.alt = altText;
      }
    }

    // Update second slide image (used for hover preview)
    const secondImg = galleryImages[1];
    if (secondImg instanceof HTMLImageElement && image2Url) {
      secondImg.src = image2Url;
      secondImg.srcset = image2Url;
      if (altText) {
        secondImg.alt = altText;
      }
      // Ensure the second slide image is loaded for smooth hover transition
      secondImg.removeAttribute('loading');
    }

    // Reset to first slide on color change so the user sees the new featured image
    const slideshow = this.#productCard.querySelector('slideshow-component');
    if (slideshow?.select && slideshow.current > 0) {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  /**
   * Save the original slide state (visibility + image data) so the gallery
   * can be fully restored when switching back to the original product.
   */
  #saveOriginalSlideState() {
    if (this.#originalSlideState) return;

    const slides = this.#getSlides();
    if (!slides) return;

    this.#originalSlideState = Array.from(slides).map((slide) => {
      const img = slide.querySelector('img');
      return {
        hidden: slide.hidden,
        src: img?.src || '',
        srcset: img?.srcset || '',
        alt: img?.alt || '',
      };
    });
  }

  /**
   * Restore every slide to its server-rendered state (image + visibility).
   */
  #restoreOriginalSlides() {
    if (!this.#originalSlideState) return;

    const slides = this.#getSlides();
    if (!slides) return;

    for (let i = 0; i < slides.length; i++) {
      const state = this.#originalSlideState[i];
      if (!state || !slides[i]) continue;

      slides[i].hidden = state.hidden;
      const img = slides[i].querySelector('img');
      if (img) {
        img.src = state.src;
        img.srcset = state.srcset;
        img.alt = state.alt;
      }
    }

    const slideshow = this.#productCard?.querySelector('slideshow-component');
    slideshow?.select?.(0, undefined, { animate: false });
  }

  /**
   * Hide all slides beyond the given count to prevent stale images from
   * the previous product showing when the user navigates the carousel.
   * @param {number} visibleCount - Number of slides to keep visible
   */
  #hideExtraSlides(visibleCount) {
    const slides = this.#getSlides();
    if (!slides) return;

    for (let i = 0; i < slides.length; i++) {
      slides[i].hidden = i >= visibleCount;
    }
  }

  /**
   * Get the slide elements from the product card's slideshow.
   * @returns {Element[]|undefined}
   */
  #getSlides() {
    const slideshow = this.#productCard?.querySelector('slideshow-component');
    return slideshow?.refs?.slides;
  }

  /**
   * Update all product-related URLs in the product card.
   * Includes the variant parameter so quick-add resolves correctly.
   * @param {string} url - The new product URL
   * @param {string} [variantId] - The variant ID to append as query param
   */
  #updateProductUrl(url, variantId) {
    if (!this.#productCard) return;

    // Build URL with variant param for quick-add compatibility
    let fullUrl = url;
    if (variantId) {
      const urlObj = new URL(url, window.location.origin);
      urlObj.searchParams.set('variant', variantId);
      fullUrl = urlObj.pathname + urlObj.search;
    }

    const { productCardLink, cardGalleryLink, productTitleLink } = this.#productCard.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = fullUrl;
    }
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = fullUrl;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = fullUrl;
    }
  }

  /**
   * Update the hidden variant ID input in the quick-add form.
   * This ensures the correct variant is added to cart.
   * @param {string} variantId - The new variant ID
   * @param {boolean} available - Whether the variant is available
   */
  #updateVariantIdInput(variantId, available) {
    if (!this.#productCard) return;

    const variantInput = this.#productCard.querySelector('input[name="id"][ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.disabled = !available;
    }
  }

  /**
   * Update the price display with the selected product's price.
   * @param {HTMLInputElement} input - The selected swatch input with price data
   */
  #updatePrice(input) {
    if (!this.#productCard) return;

    const priceContainer = this.#productCard.querySelector('product-price [ref="priceContainer"]');
    if (!priceContainer) return;

    const price = input.dataset.mpProductPrice;
    const comparePrice = input.dataset.mpProductComparePrice;

    if (!price) return;

    let priceHtml = '';

    if (comparePrice) {
      priceHtml = `
        <span role="group">
          <span class="price">${price}</span>
        </span>
        <span role="group">
          <span class="compare-at-price">${comparePrice}</span>
        </span>
      `;
    } else {
      priceHtml = `<span class="price">${price}</span>`;
    }

    priceContainer.innerHTML = priceHtml;
  }

  /**
   * Show all swatches when overflow button is clicked.
   * Called via on:click="/mpShowAllSwatches"
   * @param {Event} [event]
   */
  mpShowAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;
    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('mp-cross-product-swatches-picker')) {
  customElements.define('mp-cross-product-swatches-picker', MpCrossProductSwatchesPicker);
}
